import {
    AfterViewInit, Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild
} from '@angular/core';
import { debounce } from 'lodash';
import { FullscreenService } from '@core/services/common';
import { Subscription } from 'rxjs';

@Component({
    selector: 'caremoni-basic-bar-chart',
    templateUrl: './basic-bar-chart.component.html',
    styleUrl: './basic-bar-chart.component.scss'
})
export class BasicBarChartComponent implements OnChanges, AfterViewInit, OnDestroy {
    @ViewChild('barChartCanvas', { static: false }) canvas!: ElementRef<HTMLCanvasElement>;
    @Input() data: { value: number; color: string }[] = [];
    @Input() forceDisplayText: string = '';
    @Input() isShowValueText: boolean = false;
    @Input() groupColNumber: number = 2;
    @Input() config: any;
    @Input() labels: { type: number, configs: any[] } = { type: 1, configs: [] };
    @Input() fixColNumbers: number | undefined;
    @Input() tubeFrameIndexs: number[] = [];

    textColor: string = "#fffff";
    isDarkMode: boolean = false;
    isFullScreen: boolean = false;

    private debouncedDrawChart = debounce(() => this.drawChart(), 50);
    private observer!: MutationObserver;
    private subscription = new Subscription();

    constructor(private fullScreenService: FullscreenService) {
        this.darkModeListener();
        this.fullscreenListener();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.debouncedDrawChart();
    }

    ngOnDestroy(): void {
        if (this.observer) this.observer.disconnect();
        if (this.subscription) this.subscription.unsubscribe();
    }

    ngAfterViewInit() {
        this.darkModeChecking();
        this.debouncedDrawChart();
    }

    @HostListener('window:resize')
    onResize() {
        this.debouncedDrawChart();
    }

    drawChart() {
        if (!this.canvas) return;
        const canvasEl = this.canvas.nativeElement;
        const ctx = canvasEl.getContext('2d');
        if (!ctx) return;

        // Sync canvas internal resolution with CSS rendered size to avoid aspect ratio distortion
        const rect = canvasEl.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvasEl.width = rect.width * dpr;
        canvasEl.height = rect.height * dpr;
        ctx.scale(dpr, dpr);

        const chartWidth = rect.width;
        const chartHeight = rect.height;
        const dataLength = this.data?.length || 0;

        // Handle case where data is empty but tubeFrameIndexs exists
        const hasData = this.data && dataLength > 0;
        const hasTubeFrames = this.tubeFrameIndexs && this.tubeFrameIndexs.length > 0;

        if (!hasData && !hasTubeFrames) return;

        const breakCol = this.groupColNumber;
        const gapBetweenGroups = 10;

        // Calculate group count based on tubeFrameIndexs if no data
        let groupCount: number;
        if (hasData) {
            groupCount = Math.ceil(dataLength / breakCol);
        } else {
            groupCount = 3; // Magic number; default group of column if render data is empty
        }

        const gapCount = groupCount - 1;
        const totalGapWidth = gapBetweenGroups * gapCount;
        const barWidth = (chartWidth - totalGapWidth) / (hasData ? dataLength : (groupCount * breakCol));

        const maxValue = 10;

        ctx.clearRect(0, 0, chartWidth, chartHeight);

        // Draw bars (skip tube feeding groups) - only if data exists
        if (hasData) {
            this.data.forEach((col, index) => {
                const groupIndex = Math.floor(index / breakCol);
                const isTubeFeeding = this.tubeFrameIndexs.includes(groupIndex);

                if (isTubeFeeding) return; // Skip bars for tube feeding groups

                const groupGapsBefore = Math.floor(index / breakCol);
                const x = index * barWidth + groupGapsBefore * gapBetweenGroups;

                const barHeight = (col.value / maxValue) * chartHeight;
                const y = chartHeight - barHeight;

                ctx.fillStyle = col.color;
                ctx.fillRect(x + 2, y, barWidth - 2, barHeight);

                if (this.isShowValueText) {
                    ctx.fillStyle = this.textColor;
                    ctx.font = 'normal 5vh Arial';
                    ctx.textAlign = 'center';
                    ctx.fillText(col.value.toString(), x + barWidth / 2, y + barHeight - 20);
                }
            });
        }

        // Draw horizontal lines (skip tube feeding groups) - only if data exists
        if (hasData) {
            ctx.strokeStyle = this.textColor;
            ctx.setLineDash([5, 5]);
            ctx.lineWidth = 0.5;

            const midY = chartHeight * 0.5;
            const topY = 4 / (window.devicePixelRatio || 1);

            // Draw lines with gaps for tube feeding groups
            for (let groupIndex = 0; groupIndex < groupCount; groupIndex++) {
                const isTubeFeeding = this.tubeFrameIndexs.includes(groupIndex);
                if (isTubeFeeding) continue; // Skip lines for tube feeding groups

                const startColIndex = groupIndex * breakCol;
                const endColIndex = Math.min((groupIndex + 1) * breakCol - 1, dataLength - 1);
                const groupGapsBefore = groupIndex;

                const startX = startColIndex * barWidth + groupGapsBefore * gapBetweenGroups;
                const endX = (endColIndex + 1) * barWidth + groupGapsBefore * gapBetweenGroups;

                // Mid line
                ctx.beginPath();
                ctx.moveTo(startX, midY);
                ctx.lineTo(endX, midY);
                ctx.stroke();

                // Top line
                ctx.beginPath();
                ctx.moveTo(startX, topY);
                ctx.lineTo(endX, topY);
                ctx.stroke();
            }

            ctx.setLineDash([]);
        }

    }

    get groupIndexes(): number[] {
        const count = this.fixColNumbers
            ? this.fixColNumbers / this.groupColNumber
            : Math.ceil((this.data?.length || 0) / this.groupColNumber) || 3;
        return Array.from({ length: count }, (_, i) => i);
    }

    darkModeListener() {
        this.observer = new MutationObserver(() => this.darkModeChecking());
        this.observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    darkModeChecking() {
        const bodyClass = document.body.className;
        this.isDarkMode = bodyClass.includes("dark");
        this.textColor = this.isDarkMode ? "#fff" : "#000";
        this.debouncedDrawChart();
    }

    fullscreenListener() {
        this.subscription.add(
            this.fullScreenService.fullscreenState$.subscribe(state => {
                this.isFullScreen = state.isFullscreen;
                // Use debounced draw to avoid multiple redraws when fullscreen toggles
                // (fullscreen toggle also triggers resize event)
                this.debouncedDrawChart();
            })
        );
    }
}
